import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export type ThemePreference = 'light' | 'dark';

const STORAGE_KEY = 'unity-theme-preference';

@Injectable({ providedIn: 'root' })
export class ThemePreferenceService {
  private readonly darkMode$ = new BehaviorSubject(false);

  constructor(@Inject(PLATFORM_ID) private readonly platformId: object) {
    if (isPlatformBrowser(this.platformId)) {
      this.darkMode$.next(this.readStoredIsDark());
      this.applyDomTheme(this.darkMode$.value ? 'dark' : 'light');
    }
  }

  /** Emits whether dark mode is active (browser only; false on server). */
  get isDark$(): Observable<boolean> {
    return this.darkMode$.asObservable();
  }

  get isDark(): boolean {
    return this.darkMode$.value;
  }

  toggle(): void {
    this.setDark(!this.darkMode$.value);
  }

  setDark(dark: boolean): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    const pref: ThemePreference = dark ? 'dark' : 'light';
    localStorage.setItem(STORAGE_KEY, pref);
    this.applyDomTheme(pref);
    this.darkMode$.next(dark);
  }

  /** Apply theme from `localStorage` before the app root is created (no DI). */
  static applyStoredThemeFromLocalStorage(): void {
    if (typeof localStorage === 'undefined') {
      return;
    }
    const stored = localStorage.getItem(STORAGE_KEY) as ThemePreference | null;
    const dark = stored === 'dark';
    document.documentElement.setAttribute('data-theme', dark ? 'dark' : 'light');
  }

  private readStoredIsDark(): boolean {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'dark';
    } catch {
      return false;
    }
  }

  private applyDomTheme(pref: ThemePreference): void {
    document.documentElement.setAttribute('data-theme', pref);
  }
}
